import React, { useEffect, useState } from "react";
import api from "../lib/api";
import BottomNav from "../components/BottomNav";
import ContactLink from "../components/ContactLink";
import { Bell, MapPin, ShieldAlert, LogIn, LogOut, CheckCircle2, Navigation, Activity, ShieldCheck, Users, Link2 } from "lucide-react";
import { toast } from "sonner";

const iconFor = (kind) => {
  switch (kind) {
    case "proximity": return MapPin;
    case "geofence_enter": return LogIn;
    case "geofence_exit": return LogOut;
    case "checkin": return CheckCircle2;
    case "eta": return Navigation;
    case "health": return Activity;
    case "connection_request": return Link2;
    case "connected": return Users;
    default: return Bell;
  }
};

const colorFor = (kind) => {
  if (kind === "geofence_exit") return "text-[#FF3B30] bg-[#FF3B30]/10 border-[#FF3B30]/20";
  if (kind === "geofence_enter") return "text-[#34C759] bg-[#34C759]/10 border-[#34C759]/20";
  if (kind === "proximity") return "text-[#007AFF] bg-[#007AFF]/10 border-[#007AFF]/20";
  if (kind === "eta") return "text-[#007AFF] bg-[#007AFF]/10 border-[#007AFF]/20";
  if (kind === "health") return "text-[#FF3B30] bg-[#FF3B30]/15 border-[#FF3B30]/40";
  if (kind === "connection_request") return "text-[#FFCC00] bg-[#FFCC00]/10 border-[#FFCC00]/30";
  if (kind === "connected") return "text-[#34C759] bg-[#34C759]/10 border-[#34C759]/20";
  return "text-[#FFCC00] bg-[#FFCC00]/10 border-[#FFCC00]/20";
};

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);

  const load = async () => {
    const r = await api.get("/alerts");
    // Show only unread. Read alerts vanish immediately.
    setAlerts((r.data || []).filter((a) => !a.read));
  };
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);

  /** Optimistically remove + fire the PATCH-equivalent read call. */
  const clearAlert = async (id) => {
    setAlerts((prev) => prev.filter((a) => a.alert_id !== id));
    try { await api.post(`/alerts/${id}/read`); }
    catch { load(); /* re-sync if failed */ }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 pb-28">
      <header className="px-6 pt-10 pb-6">
        <div className="label-eyebrow flex items-center gap-2"><ShieldAlert size={12}/> Activity feed</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Alerts</h1>
      </header>

      <div className="px-6 space-y-2">
        {alerts.length === 0 && (
          <div className="p-8 rounded-2xl border border-white/5 text-center text-zinc-500">
            All quiet. We&apos;ll ping you when something happens.
          </div>
        )}
        {alerts.map((a) => {
          const Icon = iconFor(a.kind);
          const cls = colorFor(a.kind);
          return (
            <div
              key={a.alert_id}
              data-testid={`alert-${a.alert_id}`}
              onClick={() => clearAlert(a.alert_id)}
              className={`p-4 rounded-2xl border flex items-start gap-3 cursor-pointer border-white/10 hover:border-[#007AFF]/40 transition-colors`}
            >
              <div className={`size-10 rounded-full grid place-items-center border ${cls}`}>
                <Icon size={18}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold">{a.title}</div>
                <div className="text-sm text-zinc-400">{a.message}</div>
                <div className="text-xs text-zinc-600 mt-1 font-mono flex items-center gap-2">
                  <span>{new Date(a.ts).toLocaleString()}</span>
                  {(a.meta?.contact_user_id || a.meta?.user_id) && (
                    <ContactLink
                      userId={a.meta.contact_user_id || a.meta.user_id}
                      name={a.meta.contact_name || "View on map"}
                      showChevron={false}
                      testId={`alert-view-${a.alert_id}`}
                      className="text-[10px] font-bold uppercase tracking-wider text-[#007AFF] hover:text-[#4a9eff]"
                    >
                      View on map →
                    </ContactLink>
                  )}
                </div>
              </div>
              {a.kind === "health" && a.meta?.health_alert_id && !a.read && (
                <button
                  data-testid={`ack-${a.alert_id}`}
                  onClick={async (e)=>{ e.stopPropagation(); try{ await api.post(`/health/alerts/${a.meta.health_alert_id}/ack`); }catch{ /* non-fatal */ } clearAlert(a.alert_id); }}
                  className="text-[11px] font-bold uppercase tracking-wider bg-[#FF3B30] text-white px-3 py-2 rounded-full inline-flex items-center gap-1"
                >
                  <ShieldCheck size={12}/> Acknowledge
                </button>
              )}
              {a.kind === "connection_request" && !a.read && (
                <button
                  data-testid={`ack-legacy-${a.alert_id}`}
                  onClick={(e) => { e.stopPropagation(); clearAlert(a.alert_id); }}
                  className="text-[11px] font-bold uppercase tracking-wider text-zinc-400"
                  title="Legacy alert — connection already established"
                >
                  Dismiss
                </button>
              )}
              {!a.read && a.kind !== "health" && a.kind !== "connection_request" && (
                <button
                  data-testid={`mark-read-${a.alert_id}`}
                  onClick={(e) => { e.stopPropagation(); clearAlert(a.alert_id); }}
                  className="text-[11px] font-bold uppercase tracking-wider text-[#007AFF]"
                >
                  Mark read
                </button>
              )}
            </div>
          );
        })}
      </div>

      <BottomNav />
    </div>
  );
}
